import { useState, useEffect } from "react";
import TenantLayout from "@/pages/tenant/components/TenantLayout";
import { getSubdomainUrl, getDomainSuffix, getDisplayDomainSuffix } from "@/config/domain";
import { CHINA_PROVINCES, getTemplateName, TEMPLATE_NAME_MAP } from "@/constants/provinces";
import { 
  getTenantSites, 
  createTenantSite, 
  updateTenantSite, 
  deleteTenantSite,
  bindTenantSiteDomain,
  getTenantUsers,
  unbindTenantSiteDomain,
  copyTenantSite,
  type TenantSite,
  type TenantUser,
  type CopyProgress,
  type CopySiteResult,
} from "@/utils/tenantApi";

const statusLabels: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: "运行中", color: "text-green-600", bg: "bg-green-50" },
  stopped: { label: "已停止", color: "text-yellow-600", bg: "bg-yellow-50" },
  expired: { label: "已到期", color: "text-red-600", bg: "bg-red-50" },
};

const statusTabs = [
  { key: "all", label: "全部" },
  { key: "active", label: "运行中" },
  { key: "stopped", label: "已停止" },
  { key: "expired", label: "已到期" },
];

const siteTypeLabels: Record<string, { label: string; bg: string; color: string }> = {
  classified: { label: "分类信息", bg: "bg-emerald-50", color: "text-emerald-600" },
  mall: { label: "多商户商城", bg: "bg-amber-50", color: "text-amber-600" },
  anime: { label: "动漫网站", bg: "bg-rose-50", color: "text-rose-600" },
  music: { label: "音乐网站", bg: "bg-cyan-50", color: "text-cyan-600" },
};

function formatDate(dateStr: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysUntil(dateStr: string) {
  if (!dateStr) return 999;
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function TenantSitesPage() {
  const [sites, setSites] = useState<TenantSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [statusTab, setStatusTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [toast, setToast] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSite, setEditingSite] = useState<TenantSite | null>(null);
  const [isSiteAdmin, setIsSiteAdmin] = useState(false); // 是否是站点管理员角色

  // 复制站点相关状态
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyingSite, setCopyingSite] = useState<TenantSite | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [copyProgress, setCopyProgress] = useState<CopyProgress | null>(null);
  const [copyResult, setCopyResult] = useState<CopySiteResult | null>(null);

  const PAGE_SIZE = 6;

  // 获取当前用户角色
  useEffect(() => {
    try {
      const userInfo = localStorage.getItem('user_info');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        // site_admin 是站点管理员角色，不能创建站点或选择站点管理员
        setIsSiteAdmin(user.role === 'site_admin');
        console.log('当前用户角色:', user.role, '- 是否站点管理员:', user.role === 'site_admin');
      }
    } catch (e) {
      console.error('解析用户信息失败:', e);
    }
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      const res = await getTenantSites({
        page: currentPage,
        limit: PAGE_SIZE,
        status: statusTab !== 'all' ? statusTab : '',
        search: searchQuery || '',
        province: provinceFilter !== 'all' ? provinceFilter : undefined,
        template_id: templateFilter !== 'all' ? templateFilter : undefined,
      });
      setSites(res.list);
      setTotal(res.total || 0);
    } catch (err: any) {
      console.error('获取站点列表失败:', err);
      showToast(err.message || "获取数据失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, [statusTab, searchQuery, currentPage, provinceFilter, templateFilter]);

  // 切换状态/搜索时重置页码
  const handleStatusChange = (key: string) => {
    setStatusTab(key);
    setCurrentPage(1);
  };

  const handleCreateSite = async (data: { name: string; subdomain: string; site_type?: string }) => {
    try {
      await createTenantSite(data);
      showToast("站点创建成功");
      setShowCreateModal(false);
      fetchSites();
    } catch (err: any) {
      showToast(err.message || "创建失败");
    }
  };

  const handleToggleSiteStatus = async (site: TenantSite) => {
    const action = site.status === 'active' ? 'stop' : 'start';
    const confirmMsg = action === 'stop' 
      ? `确定要停止站点「${site.name}」吗？停止后该站点将无法访问。` 
      : `确定要启用站点「${site.name}」吗？`;
    if (!confirm(confirmMsg)) return;
    try {
      const newStatus = action === 'stop' ? 'stopped' : 'active';
      await updateTenantSite(site.id, { status: newStatus });
      showToast(action === 'stop' ? '站点已停止' : '站点已启用');
      fetchSites();
    } catch (err: any) {
      showToast(err.message || "操作失败");
    }
  };

  const handleDeleteSite = async (siteId: string) => {
    if (!confirm("确定要删除此站点吗？")) return;
    try {
      await deleteTenantSite(siteId);
      showToast("站点已删除");
      fetchSites();
    } catch (err: any) {
      showToast(err.message || "删除失败");
    }
  };

  const handleEditSite = async (siteId: string, data: { name: string; site_type: string; expire_at: string }) => {
    try {
      await updateTenantSite(siteId, data);
      showToast("站点信息已更新");
      setShowEditModal(false);
      setEditingSite(null);
      fetchSites();
    } catch (err: any) {
      showToast(err.message || "更新失败");
    }
  };

  const handleUpdateDomains = async (siteId: string, domain: string) => {
    try {
      await bindTenantSiteDomain(siteId, domain);
      showToast("域名绑定成功");
      setShowDomainModal(false);
      setEditingSite(null);
      fetchSites();
    } catch (err: any) {
      showToast(err.message || "绑定失败");
    }
  };

  const handleUnbindDomain = async (siteId: string, domainId: string) => {
    try {
      await unbindTenantSiteDomain(siteId, domainId);
      showToast("域名已解绑");
      fetchSites();
    } catch (err: any) {
      showToast(err.message || "解绑失败");
    }
  };

  const handleCopySite = async (data: { name: string; subdomain: string; admin_user_id?: string; expire_at?: string }) => {
    if (!copyingSite) return;

    setShowCopyModal(false);
    setShowProgressModal(true);
    setCopyResult(null);
    setCopyProgress({
      total: 10,
      current: 0,
      steps: [],
      status: 'copying',
    });

    try {
      const res = await copyTenantSite({
        source_site_id: copyingSite.id,
        name: data.name,
        subdomain: data.subdomain,
        admin_user_id: data.admin_user_id,
        expire_at: data.expire_at,
      });

      setCopyResult(res);
      setCopyProgress({
        total: res.steps.length || 10,
        current: res.steps.length || 10,
        steps: res.steps || [],
        status: 'done',
      });
      showToast("站点复制成功");
      fetchSites();
    } catch (err: any) {
      setCopyProgress(prev => prev ? { ...prev, status: 'failed', error: err.message } : {
        total: 10, current: 0, steps: [], status: 'failed', error: err.message || "复制失败",
      });
      showToast(err.message || "复制失败");
    }
  };

  const handleCloseProgress = () => {
    setShowProgressModal(false);
    setCopyingSite(null);
    setCopyProgress(null);
    setCopyResult(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <TenantLayout>
      <div className="max-w-7xl mx-auto">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="搜索站点名称、子域名或域名..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              />
            </div>
            <select
              value={provinceFilter}
              onChange={(e) => { setProvinceFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="all">全部省份</option>
              {CHINA_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={templateFilter}
              onChange={(e) => { setTemplateFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="all">全部模版</option>
              {Object.entries(TEMPLATE_NAME_MAP).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            新建站点
          </button>
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-full mb-6 w-fit">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleStatusChange(tab.key)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                statusTab === tab.key
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
              <span className="ml-1.5 text-xs text-gray-400">
                ({tab.key === "all" ? total : sites.filter(s => s.status === tab.key).length})
              </span>
            </button>
          ))}
        </div>

        {/* Sites Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? (
            <div className="col-span-full flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
            </div>
          ) : (() => {
            return sites.length > 0 ? (
              sites.map((site) => {
              const status = statusLabels[site.status] || statusLabels.active;
              const type = siteTypeLabels[site.site_type] || siteTypeLabels.classified;
              const expireDays = daysUntil(site.expire_at || '');
              return (
                <div
                  key={site.id}
                  className={`bg-white rounded-2xl border p-5 hover:border-gray-200 transition-all ${site.platform_disabled === 1 ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}
                >
                  {/* Platform disabled warning */}
                  {site.platform_disabled === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 mb-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                      <i className="ri-alert-line" />
                      该站点已被平台管理员关闭，无法自行开启
                    </div>
                  )}
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50">
                        <i className="ri-global-line text-orange-500" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800">{site.name}</h4>
                        <a
                          href={getSubdomainUrl(site.subdomain)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-orange-500 hover:text-orange-600 hover:underline mt-0.5 inline-flex items-center gap-1"
                        >
                          {site.subdomain}{getDisplayDomainSuffix()}
                          <i className="ri-external-link-line text-[10px]" />
                        </a>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Type badge + Province + Template */}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${type.bg} ${type.color}`}>
                      {type.label}
                    </span>
                    {site.province && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-teal-50 text-teal-600">
                        <i className="ri-map-pin-line text-[10px]" />
                        {site.province}
                      </span>
                    )}
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-600">
                      {getTemplateName(site.template_id)}
                    </span>
                  </div>

                  {/* Domains */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-400 mb-1.5">已绑定域名</p>
                    <div className="flex flex-wrap gap-1.5">
                      {site.domains.length > 0 ? (
                        site.domains.map((d, i) => (
                          <a
                            key={i}
                            href={`http://${d}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 hover:bg-orange-50 rounded-md text-xs text-gray-600 hover:text-orange-500 transition-colors"
                          >
                            <i className="ri-link text-xs" />
                            {d}
                            <i className="ri-external-link-line text-[10px]" />
                          </a>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">暂无绑定域名</span>
                      )}
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="space-y-1.5 mb-4 text-xs text-gray-500">
                    {site.owner_name && (
                      <div className="flex items-center justify-between">
                        <span>站点管理员</span>
                        <span className="text-orange-600 font-medium">{site.owner_name}</span>
                      </div>
                    )}
                    {site.province && (
                      <div className="flex items-center justify-between">
                        <span>所在省份</span>
                        <span className="text-gray-700 font-medium">{site.province}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span>创建时间</span>
                      <span>{formatDate(site.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>到期时间</span>
                      <span className={expireDays < 30 ? "text-red-500 font-medium" : ""}>
                        {site.expire_at ? formatDate(site.expire_at) : '永久'}
                        {expireDays < 30 && ` (${expireDays}天)`}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setEditingSite(site);
                        setShowEditModal(true);
                      }}
                      className="flex-1 py-2 text-center text-xs font-medium text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-edit-line mr-1" />
                      编辑
                    </button>
                    <button
                      onClick={() => {
                        setEditingSite(site);
                        setShowDomainModal(true);
                      }}
                      className="flex-1 py-2 text-center text-xs font-medium text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-link mr-1" />
                      域名
                    </button>
                    <button
                      onClick={() => {
                        setCopyingSite(site);
                        setShowCopyModal(true);
                      }}
                      className="px-3 py-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                      title="一键复制站点及其所有配置和数据"
                    >
                      <i className="ri-file-copy-line mr-1" />
                      复制
                    </button>
                    {site.platform_disabled !== 1 && (
                      <button
                        onClick={() => handleToggleSiteStatus(site)}
                        className={`flex-1 py-2 text-center text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                          site.status === 'active'
                            ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
                            : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                        }`}
                        title={site.status === 'active' ? '停止站点' : '启用站点'}
                      >
                        {site.status === 'active' ? (
                          <><i className="ri-stop-circle-line mr-1" />停止</>
                        ) : (
                          <><i className="ri-play-circle-line mr-1" />启用</>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteSite(site.id)}
                      className="px-3 py-2 text-xs font-medium text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="删除站点"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
              );
              })
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400">
                <i className="ri-search-2-line text-3xl mb-2" />
                <p className="text-sm">未找到匹配的站点</p>
              </div>
            );
          })()}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  p === currentPage
                    ? "bg-orange-500 text-white"
                    : "border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-500"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateSiteModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateSite}
            isSiteAdmin={isSiteAdmin}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && editingSite && (
          <EditSiteModal
            site={editingSite}
            onClose={() => {
              setShowEditModal(false);
              setEditingSite(null);
            }}
            onSave={handleEditSite}
          />
        )}

        {/* Domain Modal */}
        {showDomainModal && editingSite && (
          <DomainModal
            site={editingSite}
            onClose={() => {
              setShowDomainModal(false);
              setEditingSite(null);
            }}
            onUpdate={handleUpdateDomains}
            onUnbind={handleUnbindDomain}
          />
        )}

        {/* Copy Site Modal */}
        {showCopyModal && copyingSite && (
          <CopySiteModal
            sourceSite={copyingSite}
            isSiteAdmin={isSiteAdmin}
            onClose={() => {
              setShowCopyModal(false);
              setCopyingSite(null);
            }}
            onCopy={handleCopySite}
          />
        )}

        {/* Copy Progress Modal */}
        {showProgressModal && (
          <CopyProgressModal
            progress={copyProgress}
            result={copyResult}
            sourceName={copyingSite?.name || ''}
            onClose={handleCloseProgress}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
            <i className="ri-checkbox-circle-line text-green-400" />
            {toast}
          </div>
        )}
      </div>
    </TenantLayout>
  );
}

/* ── Create Site Modal ── */
function CreateSiteModal({ onClose, onCreate, isSiteAdmin }: { onClose: () => void; onCreate: (data: any) => void; isSiteAdmin: boolean }) {
  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [siteType, setSiteType] = useState("classified");
  const [province, setProvince] = useState("");
  const [expireDate, setExpireDate] = useState("");
  const [adminUser, setAdminUser] = useState("");
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 加载可选的管理员用户列表（仅非站点管理员角色需要选择）
  useEffect(() => {
    // 站点管理员角色不需要选择管理员，创建者即为管理员
    if (isSiteAdmin) {
      return;
    }
    
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await getTenantUsers({ limit: 100 });
        setUsers(res.list);
      } catch (e) {
        console.error("加载用户列表失败", e);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [isSiteAdmin]);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("请输入站点名称"); return; }
    if (!subdomain.trim()) { setError("请输入子域名标识"); return; }
    if (!/^\w{2,20}$/.test(subdomain)) { setError("子域名只能包含字母、数字、下划线，长度2-20位"); return; }

    setSubmitting(true);
    try {
      const data: any = { 
        name: name.trim(), 
        subdomain: subdomain.trim().toLowerCase(), 
        site_type: siteType,
        province: province || undefined,
      };
      if (expireDate) {
        const d = new Date(expireDate);
        data.expire_at = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} 23:59:59`;
      }
      if (adminUser) {
        data.admin_user_id = adminUser;
      }
      await onCreate(data);
    } finally {
      setSubmitting(false);
    }
  };

  // 计算默认到期时间（3个月后）
  const defaultExpireDate = new Date();
  defaultExpireDate.setMonth(defaultExpireDate.getMonth() + 3);
  const defaultDateStr = defaultExpireDate.toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">新建站点</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点名称 <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如：北京分站"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">子域名标识 <span className="text-red-500">*</span></label>
            <div className="flex items-center">
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                placeholder="如：beijing"
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-l-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <span className="px-4 py-2.5 bg-gray-100 border border-l-0 border-gray-200 rounded-r-xl text-sm text-gray-500">
                {getDomainSuffix()}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点类型</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(siteTypeLabels).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => setSiteType(key)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-xl border transition-all cursor-pointer ${
                    siteType === key
                      ? "border-orange-300 bg-orange-50 text-orange-600"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {siteType === key ? (
                    <i className="ri-checkbox-circle-fill text-orange-500" />
                  ) : (
                    <i className="ri-checkbox-blank-circle-line text-gray-300" />
                  )}
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">所在省份</label>
            <select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 cursor-pointer"
            >
              <option value="">请选择省份（可选）</option>
              {CHINA_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          {!isSiteAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">站点管理员</label>
              <select
                value={adminUser}
                onChange={(e) => setAdminUser(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="">请选择管理员（可选）</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.nickname || u.username} (@{u.username})</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">不选择则默认为当前代理商账号</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">到期时间</label>
            <input
              type="date"
              value={expireDate || defaultDateStr}
              onChange={(e) => setExpireDate(e.target.value)}
              min={defaultDateStr}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <p className="text-xs text-gray-400 mt-1">留空则默认3个月后到期</p>
          </div>
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '创建中...' : '创建站点'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Site Modal ── */
function EditSiteModal({ site, onClose, onSave }: { site: TenantSite; onClose: () => void; onSave: (siteId: string, data: { name: string; site_type: string; expire_at: string }) => void }) {
  const [name, setName] = useState(site.name);
  const [siteType, setSiteType] = useState(site.site_type || 'classified');
  const [expireDate, setExpireDate] = useState(site.expire_at ? site.expire_at.slice(0, 10) : '');
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("请输入站点名称"); return; }
    setSubmitting(true);
    try {
      const data: any = { name: name.trim(), site_type: siteType };
      if (expireDate) {
        const d = new Date(expireDate);
        data.expire_at = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} 23:59:59`;
      } else {
        data.expire_at = ''; // 清空到期时间
      }
      await onSave(site.id, data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">编辑站点 - {site.name}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点名称 <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">子域名</label>
            <input
              type="text"
              value={site.subdomain}
              disabled
              className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-xl text-sm text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-400 mt-1">子域名创建后不可修改</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点类型</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(siteTypeLabels).map(([key, t]) => (
                <button
                  key={key}
                  onClick={() => setSiteType(key)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-xl border transition-all cursor-pointer ${
                    siteType === key
                      ? "border-orange-300 bg-orange-50 text-orange-600"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {siteType === key ? (
                    <i className="ri-checkbox-circle-fill text-orange-500" />
                  ) : (
                    <i className="ri-checkbox-blank-circle-line text-gray-300" />
                  )}
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">到期时间</label>
            <input
              type="date"
              value={expireDate}
              onChange={(e) => setExpireDate(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <p className="text-xs text-gray-400 mt-1">留空则为永久有效</p>
          </div>
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '保存中...' : '保存修改'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Domain Modal ── */
function DomainModal({
  site,
  onClose,
  onUpdate,
  onUnbind,
}: {
  site: TenantSite;
  onClose: () => void;
  onUpdate: (siteId: string, domain: string) => void;
  onUnbind?: (siteId: string, domainId: string) => void;
}) {
  const [domain, setDomain] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [domains, setDomains] = useState<{id: string; domain: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [wwwPreview, setWwwPreview] = useState("");

  useEffect(() => {
    // 从 site.domains 加载（简化处理，实际应该从 API 获取）
    setDomains(site.domains.map((d, i) => ({ id: `d${i}`, domain: d })));
    setLoading(false);
  }, [site]);

  // 实时预览自动www域名
  const updateWwwPreview = (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (!trimmed || !/^[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/.test(trimmed)) {
      setWwwPreview("");
      return;
    }
    const parts = trimmed.split('.');
    const existingDomains = domains.map(d => d.domain);
    if (parts.length >= 2 && parts[0] !== 'www') {
      const wwwDomain = 'www.' + trimmed;
      if (!existingDomains.includes(wwwDomain)) {
        setWwwPreview(wwwDomain);
        return;
      }
    }
    if (parts.length >= 3 && parts[0] === 'www') {
      const rootDomain = parts.slice(1).join('.');
      if (!existingDomains.includes(rootDomain)) {
        setWwwPreview(rootDomain);
        return;
      }
    }
    setWwwPreview("");
  };

  const handleSubmit = async () => {
    if (!domain.trim()) { setError("请输入域名"); return; }
    if (!/^[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+$/.test(domain.trim())) {
      setError("域名格式无效");
      return;
    }

    setSubmitting(true);
    try {
      await onUpdate(site.id, domain.trim().toLowerCase());
      setDomain("");
    } catch (err: any) {
      setError(err.message || "绑定失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnbind = async (domainId: string, domainName: string) => {
    if (!confirm(`确定要解绑域名 "${domainName}" 吗？`)) return;
    try {
      await onUnbind?.(site.id, domainName);
    } catch (err: any) {
      alert(err.message || "解绑失败");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">域名管理 - {site.name}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-information-line text-blue-500" />
            <span className="text-sm font-medium text-blue-700">域名解析设置指引</span>
          </div>
          <div className="space-y-2 text-xs text-blue-600 leading-relaxed">
            <p>1. 在下方输入您的域名完成绑定</p>
            <p>2. 前往您的 DNS 服务商（如阿里云、腾讯云）添加解析记录：</p>
            <div className="bg-white/60 p-2 rounded-lg font-mono text-xs">
              <p><b>记录类型</b>：A</p>
              <p><b>主机记录</b>：@（或 www）</p>
              <p><b>记录值</b>：服务器 IP 地址</p>
            </div>
            <p>3. 等待 DNS 生效后（通常几分钟），即可访问您的域名</p>
            <p className="text-blue-500">系统已自动将域名同步到服务器配置，无需等待人工处理。</p>
          </div>
        </div>

        {/* 已绑定域名列表 */}
        {!loading && domains.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">已绑定域名</label>
            <div className="space-y-2">
              {domains.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                  <a
                    href={`http://${d.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-700 hover:text-orange-500"
                  >
                    <i className="ri-link text-gray-400" />
                    {d.domain}
                    <i className="ri-external-link-line text-[10px] text-gray-400" />
                  </a>
                  <button
                    onClick={() => handleUnbind(d.id, d.domain)}
                    className="text-xs text-red-500 hover:text-red-600 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 自动www域名预览 */}
        {wwwPreview && (
          <div className="p-3 bg-green-50 rounded-xl border border-green-200 mb-4 flex items-center gap-2">
            <i className="ri-check-double-line text-green-500" />
            <span className="text-sm text-green-700">
              将同时绑定 <b className="font-mono">{wwwPreview}</b>
            </span>
          </div>
        )}

        {/* 绑定新域名 */}
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">绑定新域名</label>
            <input
              type="text"
              value={domain}
              onChange={(e) => { setDomain(e.target.value); updateWwwPreview(e.target.value); }}
              placeholder="如：www.example.com"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            关闭
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '绑定中...' : '绑定域名'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Copy Site Modal ── */
function CopySiteModal({ sourceSite, isSiteAdmin, onClose, onCopy }: { 
  sourceSite: TenantSite; 
  isSiteAdmin: boolean; 
  onClose: () => void; 
  onCopy: (data: any) => void;
}) {
  const [name, setName] = useState(sourceSite.name + '-副本');
  const [subdomain, setSubdomain] = useState("");
  const [expireDate, setExpireDate] = useState("");
  const [adminUser, setAdminUser] = useState("");
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sourceType = sourceSite.site_type || 'classified';
  const typeLabel = siteTypeLabels[sourceType]?.label || '分类信息';

  useEffect(() => {
    if (isSiteAdmin) return;
    
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await getTenantUsers({ limit: 100 });
        setUsers(res.list);
      } catch (e) {
        console.error("加载用户列表失败", e);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [isSiteAdmin]);

  const handleSubmit = async () => {
    if (!name.trim()) { setError("请输入站点名称"); return; }
    if (!subdomain.trim()) { setError("请输入子域名标识"); return; }
    if (!/^\w{2,20}$/.test(subdomain)) { setError("子域名只能包含字母、数字、下划线，长度2-20位"); return; }

    setSubmitting(true);
    try {
      const data: any = { 
        name: name.trim(), 
        subdomain: subdomain.trim().toLowerCase(),
      };
      if (expireDate) {
        const d = new Date(expireDate);
        data.expire_at = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} 23:59:59`;
      }
      if (adminUser) {
        data.admin_user_id = adminUser;
      }
      await onCopy(data);
    } catch (err: any) {
      setError(err.message || "复制失败");
    } finally {
      setSubmitting(false);
    }
  };

  const defaultExpireDate = new Date();
  defaultExpireDate.setMonth(defaultExpireDate.getMonth() + 3);
  const defaultDateStr = defaultExpireDate.toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">复制站点</h3>
            <p className="text-xs text-gray-400 mt-0.5">基于「{sourceSite.name}」创建副本</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        {/* 复制范围提示 */}
        <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 mb-4">
          <div className="flex items-start gap-2">
            <i className="ri-information-line text-blue-500 mt-px" />
            <div className="text-xs text-blue-600 leading-relaxed">
              <p className="font-medium mb-1">将复制以下内容到新站点：</p>
              <p>网站设置、分类、单页、导航菜单、装修配置、支付配置、注册字段、短信配置、VIP套餐、发布内容</p>
              <p className="mt-1 text-blue-400">域名绑定、站点管理员、历史订单不会复制</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点名称 <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="新站点名称"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">子域名标识 <span className="text-red-500">*</span></label>
            <div className="flex items-center">
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                placeholder="如：beijing-copy"
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-l-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <span className="px-4 py-2.5 bg-gray-100 border border-l-0 border-gray-200 rounded-r-xl text-sm text-gray-500">
                {getDomainSuffix()}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">站点类型</label>
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${siteTypeLabels[sourceType]?.bg || 'bg-emerald-50'} ${siteTypeLabels[sourceType]?.color || 'text-emerald-600'}`}>
                {typeLabel}
              </span>
              <span className="text-xs text-gray-400">站点类型与被复制站点保持一致，不可切换</span>
            </div>
          </div>
          {!isSiteAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">站点管理员</label>
              <select
                value={adminUser}
                onChange={(e) => setAdminUser(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">请选择管理员（可选）</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.nickname || u.username} (@{u.username})</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">不选择则默认为当前代理商账号</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">到期时间</label>
            <input
              type="date"
              value={expireDate || defaultDateStr}
              onChange={(e) => setExpireDate(e.target.value)}
              min={defaultDateStr}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-400 mt-1">留空则默认3个月后到期</p>
          </div>
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '创建中...' : '创建并复制站点'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Copy Progress Modal ── */
function CopyProgressModal({ progress, result, sourceName, onClose }: { 
  progress: CopyProgress | null; 
  result: CopySiteResult | null;
  sourceName: string;
  onClose: () => void;
}) {
  const isDone = progress?.status === 'done';
  const isFailed = progress?.status === 'failed';
  const percent = progress ? Math.round((progress.current / Math.max(progress.total, 1)) * 100) : 0;

  // Build display steps from progress or result
  const displaySteps = progress?.steps?.length
    ? progress.steps
    : [
        { name: '创建站点记录', status: 'pending' as const },
        { name: '复制站点设置', status: 'pending' as const },
        { name: '复制分类', status: 'pending' as const },
        { name: '复制单页', status: 'pending' as const },
        { name: '复制导航菜单', status: 'pending' as const },
        { name: '复制装修配置', status: 'pending' as const },
        { name: '复制支付配置', status: 'pending' as const },
        { name: '复制注册字段', status: 'pending' as const },
        { name: '复制短信配置', status: 'pending' as const },
        { name: '复制VIP套餐', status: 'pending' as const },
        { name: '复制发布内容', status: 'pending' as const },
      ];

  const stepIcon = (status: string) => {
    if (status === 'done') return <i className="ri-checkbox-circle-fill text-green-500" />;
    if (status === 'failed') return <i className="ri-close-circle-fill text-red-500" />;
    if (status === 'running') return <i className="ri-loader-4-line animate-spin text-blue-500" />;
    return <i className="ri-checkbox-blank-circle-line text-gray-300" />;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {isDone ? '复制完成' : isFailed ? '复制失败' : '正在复制站点'}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">来源：{sourceName}</p>
          </div>
          {(isDone || isFailed) && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-400" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              {isFailed ? '复制过程中出现错误' : isDone ? '全部完成' : '处理中...'}
            </span>
            <span className={`text-sm font-bold ${isDone ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-blue-600'}`}>
              {percent}%
            </span>
          </div>
          <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${isFailed ? 'bg-red-500' : isDone ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="max-h-64 overflow-y-auto space-y-1.5">
          {displaySteps.map((step, i) => (
            <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${
              step.status === 'done' ? 'text-gray-600' : 
              step.status === 'running' ? 'text-blue-600 bg-blue-50' : 
              step.status === 'failed' ? 'text-red-600 bg-red-50' : 
              'text-gray-400'
            }`}>
              <span className="flex-shrink-0">{stepIcon(step.status)}</span>
              <span className="flex-1">{step.name}</span>
              {step.count !== undefined && step.status === 'done' && (
                <span className="text-xs text-gray-400">{step.count}条</span>
              )}
            </div>
          ))}
        </div>

        {/* Error message */}
        {isFailed && progress?.error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-xl">
            <p className="font-medium mb-0.5">错误信息</p>
            <p className="text-xs">{progress.error}</p>
          </div>
        )}

        {/* Stats summary */}
        {isDone && result?.stats && (
          <div className="mt-4 p-3 bg-green-50 rounded-xl border border-green-100">
            <p className="text-sm font-medium text-green-700 mb-2">复制统计</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-green-600">
              {result.stats.categories > 0 && <span>📂 {result.stats.categories} 个分类</span>}
              {result.stats.single_pages > 0 && <span>📄 {result.stats.single_pages} 个单页</span>}
              {result.stats.nav_menus > 0 && <span>🧭 {result.stats.nav_menus} 个导航</span>}
              {result.stats.register_fields > 0 && <span>📝 {result.stats.register_fields} 个注册字段</span>}
              {result.stats.vip_plans > 0 && <span>💎 {result.stats.vip_plans} 个VIP套餐</span>}
              {result.stats.listings > 0 && <span>📋 {result.stats.listings} 条发布内容</span>}
            </div>
          </div>
        )}

        {/* Close button */}
        {(isDone || isFailed) && (
          <div className="mt-6">
            <button
              onClick={onClose}
              className="w-full py-2.5 text-sm font-medium text-white bg-gray-700 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer"
            >
              {isDone ? '完成' : '关闭'}
            </button>
          </div>
        )}

        {/* Copying spinner */}
        {!isDone && !isFailed && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-400">
            <i className="ri-loader-4-line animate-spin" />
            请耐心等待，正在复制数据...
          </div>
        )}
      </div>
    </div>
  );
}
